import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { Card, Stack, styled } from "~/common/mui";
import { useTranslation } from "react-i18next";
import type { LineItem } from "~/features/cart/cart/models/CartData";
import {
  ContractItemParam,
  QuoteItemParam,
  userCanSeePriceReference,
} from "~/features/cart/cart/components/LineItemStaticParams";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";
import { Parameter } from "~/common/components/ParameterV2";
import { getEDDLabel } from "~/features/cart/cart/utils/cartUtils";
import { LineItemMessagesSection } from "~/domains/cart/components";
import { ViewablePackagingCell } from "~/features/cart/cart/components/PackagingCell";
import { LineItemStockIndicator } from "~/features/cart/cart/components/LineItemStockIndicator";
import { NumericFormat } from "react-number-format";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { ProductParams2 } from "~/domains/product/components/ProductParamsV2";
import { PartialDeliveriesSection } from "./PartialDeliveriesSection";
import { IncludedSurchargeAsterisk, TariffSurchargeAsterisk, OptionalBox } from "./commonComponents";
import { isLineLevelCustomerOrderNumberEnabled } from "~/utils/features";
import { FormStaticText } from "~/common/components/FormStaticText/FormStaticText";
import { ProductLineGrid } from "./ProductLineGrid";
import { KitPartsSection, type ResolvedOptionalPart } from "./components/KitPartsSection";
import type { ProductLineViewModel } from "./models/ProductTableViewModel";
import { useSession } from "~/app/providers/SessionProvider";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { OptionalPartEntry } from "./OptionalPartEntry";

interface CheckoutLineItemProps {
  className?: string;
  line: ProductLineViewModel;
  product: ProductDescriptor;
  housingKit?: boolean;
}

const TotalPrice = styled("strong")({
  fontWeight: "bold",
});

export function CheckoutProductLine({ className, line, product, housingKit }: CheckoutLineItemProps) {
  const { t } = useTranslation();
  const { selection } = usePriceReferenceSelection();

  const { pricing, flags, formatted, kit, messages } = line;
  const lineItem = line.sourceLineItem as LineItem;
  const lineItemIsFromContract = !!lineItem.contractNumber;
  const { quantityVisible, isPMA, isOptime, webservicesDisabled } = flags;
  const { totalPriceFormatted, hasSurcharge, surchargeValue, tariffSurcharge, zeroPriceLabel } = pricing;
  const zeroText = zeroPriceLabel
    ? t(zeroPriceLabel.key, { ...zeroPriceLabel.params, defaultValue: zeroPriceLabel.fallback })
    : undefined;

  const showPriceReference = userCanSeePriceReference(selection);
  const targetPriceEnabled = useSession()?.commerceContext.targetPriceEnabled ?? false;
  const onSaleProduct = useSalesHelper(product).isProductOnSale();
  const hasTargetPrice = lineItem.targetPrice && lineItem.targetPrice.value > 0 ? true : false;

  const showEdd = true;

  const aliases = ProductParams2.ProductAliases({
    mode: "15-digits",
    visibleAliasesLayout: "separate-rows",
    packCode: lineItem.packCode,
  })({
    t,
    product,
  });

  const gpsd = ProductParams2.GPSD({
    t,
    product,
  });

  return (
    <Card sx={{ p: 3 }} component={Stack} gap={3} className={className}>
      <ProductLineGrid>
        <ProductLineGrid.StockIndicator>
          <LineItemStockIndicator product={product} packEntry={lineItem} />
        </ProductLineGrid.StockIndicator>

        <ProductLineGrid.Quantity>
          <Parameter
            label={t("input.quantity")}
            value={
              <NumericFormat
                value={formatted.quantity}
                displayType="text"
                thousandSeparator={NF.getThousandSeparator()}
                decimalSeparator={NF.getDecimalSeparator()}
              />
            }
          />
        </ProductLineGrid.Quantity>

        <ProductLineGrid.NetPrice>
          {!hasTargetPrice && (
            <Parameter
              label={<PriceElements.NetPriceLabel variant="inherit" price={lineItem.netPrice} />}
              value={
                webservicesDisabled ? null : (
                  <PriceElements.Stack gap={0}>
                    <PriceElements.Price
                      variant="inherit"
                      price={lineItem.netPrice}
                      zeroText={zeroText}
                      tariffSurcharge={tariffSurcharge}
                    />
                  </PriceElements.Stack>
                )
              }
            />
          )}

          {!lineItemIsFromContract &&
            !lineItem.quoteNumber &&
            !kit &&
            !housingKit &&
            !onSaleProduct &&
            targetPriceEnabled &&
            hasTargetPrice && (
              <Parameter
                label={<PriceElements.TargetPriceLabel variant="inherit" price={lineItem.targetPrice} />}
                value={<PriceElements.Price variant="inherit" price={lineItem.targetPrice} />}
              />
            )}
        </ProductLineGrid.NetPrice>

        <ProductLineGrid.TotalPrice>
          <Parameter
            label={<PriceElements.TotalPriceLabel variant="inherit" price={lineItem.netPrice} />}
            value={
              <Stack>
                {!webservicesDisabled && (
                  <TotalPrice>
                    {totalPriceFormatted}
                    {hasSurcharge && "*"}
                  </TotalPrice>
                )}
              </Stack>
            }
          />
        </ProductLineGrid.TotalPrice>

        <ProductLineGrid.DeliveryDate>
          {quantityVisible && !isPMA ? (
            <FormStaticText
              label={t("cartPage.table.header.delivery.title")}
              value={formatted.deliveryDate}
              helperText={showEdd ? getEDDLabel(lineItem, t) : "-"}
            />
          ) : (
            <FormStaticText label={t("cartPage.table.header.delivery.title")} value={"-"} />
          )}
        </ProductLineGrid.DeliveryDate>

        <ProductLineGrid.Packaging>
          <ViewablePackagingCell lineItem={lineItem} product={product} />
        </ProductLineGrid.Packaging>

        {showPriceReference && (
          <ProductLineGrid.PriceReference>
            <Parameter label={t("cartPage.table.header.priceReference.title")} value={lineItem.priceReference || "-"} />
          </ProductLineGrid.PriceReference>
        )}

        {aliases && (
          <ProductLineGrid.Aliases>
            <Parameter {...aliases} />
          </ProductLineGrid.Aliases>
        )}

        {gpsd && (
          <ProductLineGrid.GPSD>
            <Parameter {...gpsd} />
          </ProductLineGrid.GPSD>
        )}

        <ProductLineGrid.QuoteDetails>
          <QuoteItemParam product={product.base} lineItem={lineItem} />
          <ContractItemParam product={product.base} lineItem={lineItem} />
        </ProductLineGrid.QuoteDetails>

        {isLineLevelCustomerOrderNumberEnabled() && lineItem.customerOrderNumber && (
          <ProductLineGrid.PONumber>
            <Parameter label={t("search.type.customerOrderNumber")} value={lineItem.customerOrderNumber} />
          </ProductLineGrid.PONumber>
        )}

        <ProductLineGrid.Messages>
          <LineItemMessagesSection
            messages={messages}
            isPMA={isPMA}
            isOptime={isOptime}
            quantityVisible={quantityVisible}
          />
        </ProductLineGrid.Messages>
      </ProductLineGrid>

      <OptionalBox>
        {surchargeValue && <IncludedSurchargeAsterisk value={surchargeValue} percent={lineItem.netPrice?.surcharge} />}
        {tariffSurcharge && <TariffSurchargeAsterisk />}
      </OptionalBox>

      {kit && (
        <KitPartsSection
          kit={kit}
          renderOptionalPart={(part) => <ViewableOptionalPartWrapper key={part.rowId} part={part} />}
        />
      )}

      <PartialDeliveriesSection partialDeliveries={lineItem.partialDeliveries} />

      {lineItem.comment && (
        <Parameter
          label={t("wishlistPage.comment.label")}
          value={lineItem.comment}
          slotProps={{ value: { variant: "bodyMedium" } }}
        />
      )}
    </Card>
  );
}

const ViewableOptionalPartLine = styled(CheckoutProductLine)(({ theme }) => ({
  border: "none",
  borderTop: "1px solid",
  borderRadius: 0,
  borderColor: theme.palette.divider,
}));

function ViewableOptionalPartWrapper({ part }: { part: ResolvedOptionalPart }) {
  return (
    <OptionalPartEntry descriptor={part.descriptor} editMode={false}>
      <ViewableOptionalPartLine
        housingKit={true}
        line={part.viewModel}
        product={part.descriptor}
      />
    </OptionalPartEntry>
  );
}
